/**
 * Lander picker — mirrors the Binom "Choose lander" modal: filter row
 * (group + language + search) + sortable table sorted by ↓id.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo, useState } from 'react';

import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/layout/Drawer';
import { Button } from '@/components/ui/layout/Button';
import { SearchableSelect } from '@/components/ui/form/input/SearchableSelect';
import {
  useBinomGroups,
  useBinomLandings,
  useBinomLanguages,
  type BinomLanding,
} from '@/hooks/binom';

import BinomLanderPickerTable from './picker-shared/tables/BinomLanderPickerTable';
import PickerFilterRow from './picker-shared/PickerFilterRow';
import {
  groupFilterOptions,
  groupNameMap,
  languageFilterOptions,
} from './picker-shared/picker-filter-options';
import { sortByIdDesc } from './picker-shared/sort-by-id-desc';

export interface BinomLanderPickerProps {
  open: boolean;
  onClose: () => void;
  excludeIds: string[];
  onAttach: (landings: BinomLanding[]) => void;
}

const BinomLanderPicker: FC<BinomLanderPickerProps> = ({
  open,
  onClose,
  excludeIds,
  onAttach,
}) => {
  const [search, setSearch] = useState('');
  const [groupUuid, setGroupUuid] = useState('');
  const [language, setLanguage] = useState('');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const landingsQuery = useBinomLandings(search);
  const groupsQuery = useBinomGroups('landings');
  const languagesQuery = useBinomLanguages();

  const groupOptions = useMemo(() => groupFilterOptions(groupsQuery.data), [groupsQuery.data]);
  const languageOptions = useMemo(() => languageFilterOptions(languagesQuery.data), [languagesQuery.data]);
  const groupNameByUuid = useMemo(() => groupNameMap(groupsQuery.data), [groupsQuery.data]);

  const rows = useMemo(() => {
    const list = Array.isArray(landingsQuery.data) ? landingsQuery.data : [];
    const filtered = list
      .filter((l) => !excludeIds.includes(l.id))
      .filter(
        (l) => !groupUuid || (l as { groupUuid?: string }).groupUuid === groupUuid,
      )
      .filter((l) => !language || l.languageCode === language);
    return sortByIdDesc(filtered);
  }, [landingsQuery.data, excludeIds, groupUuid, language]);

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const close = () => {
    setSelected({});
    setSearch('');
    setGroupUuid('');
    setLanguage('');
    onClose();
  };
  const submit = () => {
    const picks = rows.filter((l) => selected[l.id]);
    if (picks.length > 0) onAttach(picks);
    close();
  };

  return (
    <Drawer open={open} onOpenChange={(o) => !o && close()}>
      <DrawerContent variant="center" width="w-[44rem]">
        <DrawerHeader>
          <DrawerTitle>Choose lander</DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          <PickerFilterRow
            primary={
              <SearchableSelect
                value={groupUuid}
                onChange={(v) => setGroupUuid(String(v))}
                options={groupOptions}
                placeholder="All groups"
              />
            }
            search={search}
            onSearchChange={setSearch}
            extras={
              <div className="min-w-[10rem] flex-1">
                <SearchableSelect
                  value={language}
                  onChange={(v) => setLanguage(String(v))}
                  options={languageOptions}
                  placeholder="All languages"
                />
              </div>
            }
          />
          <BinomLanderPickerTable
            rows={rows}
            loading={landingsQuery.isLoading}
            selected={selected}
            onToggle={(id, v) => setSelected((s) => ({ ...s, [id]: v }))}
            groupNameByUuid={groupNameByUuid}
          />
        </DrawerBody>
        <DrawerFooter>
          <Button type="button" variant="outline" onClick={close} size="sm">
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={selectedCount === 0}
            onClick={submit}
          >
            Apply {selectedCount > 0 ? `(${selectedCount})` : ''}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default BinomLanderPicker;
